import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { trackLineItemChange } from '../../utils/tracking';

import Button from '../../atoms/button';
import Select from '../../atoms/select';
import Typography from '../../atoms/typography';

import useObserver from '../../utils/hooks/useObserver';

import { useProduct } from '../../utils/context/product-provider';
import { useSiteSpect } from '../../utils/context/sitespect-provider';

const SubmitButton = styled(Button)`
  outline: 0;
  padding: 0 1rem;
  width: 100%;
  ${({ displayOpacity }) => (displayOpacity && css`
  :hover {
    opacity: 1;
  }
  `)}
`;

const ButtonWrapper = styled.span`
  display: flex;
  flex-direction: center;
  width: 90%;
  height: 100%;

  ${({ hasVariants }) => hasVariants && css`
    width: 70%;
  `}
`;

const SizeSelect = styled(Select)`
  color: ${(props) => props.theme.color.brand};
  outline: 0;
  width: 25%;
  height: 100%;
  font-size: ${(props) => props.theme.modularScale.fourteen};
  border: 0.1rem solid ${({ showWarningMsg, theme }) => (showWarningMsg ? theme.color.brandA11yRed : theme.color.borderBlue)};
  `;

const WarningText = styled(Typography)`
  font-family: ${(props) => props.theme.font.sans};
  font-size: 1.5rem;
  color: ${(props) => props.theme.color.brandA11yRed};
  display: block;
  line-height: 1.5;
`;

const WarningWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.2rem 0;
`;

const CallToActionWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0 2vh;
  `;

const StickyMobileProductAdd = styled.div`
  position: fixed;
  left: 0;
  bottom: 0;
  height: ${({ showWarningMsg }) => (showWarningMsg ? '15%' : '10%')};
  width: 100%;
  display: ${({ notAvailable }) => (notAvailable ? 'none' : 'flex')};
  flex-direction: column;
  justify-content: ${(showWarningMsg) => (showWarningMsg ? 'space-evenly' : 'space-around')};
  background-color: white;
  box-shadow: 0px -2px 16px rgba(0,0,0,0.1);
  z-index: ${(props) => props.theme.layers.downstage - 1};

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: none;
  }

  ${({ isVisible, theme }) => (isVisible ? css`
    animation: ${theme.animations.stickyAppear} ${theme.animation.fast};
  ` : css`
    animation: ${theme.animations.stickyDisappear} ${theme.animation.fast} forwards;
  `
  )}
`;

const StickyProductAdd = (props) => {
  const { pdpRevamp = false } = useSiteSpect();

  const {
    state: {
      warning,
      quantityChange,
      buttonText,
      stickyAddedToCart,
      onPageAddedToCart,
      requireMonogram: contextRequireMonogram,
      wishlistRef,
      monogramFormRef,
      onPageCTARef
    },
    updateWarningMessage,
    onStickyAddedToCart
  } = useProduct();

  const getOptionType = () => props.product?.option_types?.[0]?.name || 'Size';
  const hasVariants = () => props.product?.variants?.length > 1;

  const [addingToCart, setAddingToCart] = useState(false);
  const [requireMonogram, setRequireMonogram] = useState(contextRequireMonogram);
  const [displayButtonText, setDisplayButtonText] = useState('Add to Bag');
  const [optionOutOfStock, setOptionOutOfStock] = useState(false);
  const [option, setOption] = useState('0');
  const [optionType] = useState(getOptionType());
  const [outOfStock, setOutOfStock] = useState(false);
  const [variantChanged, setVariantChanged] = useState(false);
  const isVisible = useObserver(onPageCTARef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5
  });

  const canSubmit = () => {
    if (props.cart.loading) return false;

    if (outOfStock || optionOutOfStock) return 'OOS';
    if (requireMonogram) return 'Please provide all monogram options';
    if (option === null) return `Please select a ${optionType.toLowerCase()}`;

    return true;
  };

  const handleOnSelectChange = (e) => {
    const { variants } = props.product;

    setOption(e.currentTarget.value);
    updateWarningMessage('');

    if (e.currentTarget.value.includes('(SOLD OUT)')) {
      setOptionOutOfStock(true);
      setDisplayButtonText('Out Of Stock - Notify Me');
    } else {
      setVariantChanged(true);
      setOptionOutOfStock(false);
    }

    // update the variant with the user-selected variant
    let newVariant;
    if (e.currentTarget.value.includes('(')) {
      const trimmedValue = e.currentTarget.value.split('(')[0].trim();
      newVariant = variants.find((v) => v.option_values[0].name === trimmedValue);
    } else {
      newVariant = variants.find((v) => v.option_values[0].name === e.currentTarget.value);
    }

    props.onVariantChange(newVariant);
    setVariantChanged(true);

    if (!e.currentTarget.value.includes('(SOLD OUT)')) {
      trackLineItemChange({
        product: props.product,
        quantity: quantityChange,
        variant: newVariant
      });
    }
  };

  const renderSizeOptions = () => {
    if (hasVariants()) {
      const { variants } = props.product;
      const options = [];

      variants.forEach((v) => {
        let oosText = '';

        if (v.total_on_hand === 1) oosText = '(Only 1 Left!)';
        if (v.total_on_hand === 0) oosText = '(SOLD OUT)';

        options.push(<option key={v.option_values[0].name.trim()}>{`${v.option_values[0].name} ${oosText}`}</option>);
      });

      return options;
    }

    return [];
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();

    const validSubmission = canSubmit();

    if (validSubmission === true) {
      setAddingToCart(true);
      onStickyAddedToCart(true);
    } else if (validSubmission.includes('Please')) {
      updateWarningMessage(validSubmission);

      if (requireMonogram) {
        global.window.scroll({
          left: 0,
          top: monogramFormRef.getBoundingClientRect().top
            + global.window.scrollY
            - (global.window.innerHeight / 5),
          behavior: 'smooth'
        });
      }
    } else if (validSubmission.includes('OOS')) {
      global.window.scroll({
        left: 0,
        top: wishlistRef.getBoundingClientRect().top
          + global.window.scrollY
          - (global.window.innerHeight / 2),
        behavior: 'smooth'
      });
    }
  };

  const buttonDisabled = () => addingToCart || onPageAddedToCart || props.cart.loading || displayButtonText === 'No More Left';

  const isProductAvailable = () => props.product.available;
  const isProductDiscontinued = () => props.product.discontinued;

  useEffect(() => {
    const { variant: chosenVariant } = props;

    // to handle a selection from on-page CTA
    if (chosenVariant) {
      let oosText = '';

      if (chosenVariant.total_on_hand === 1) oosText = '(Only 1 Left!)';
      if (chosenVariant.total_on_hand === 0) oosText = '(SOLD OUT)';

      setOption(`${chosenVariant.option_values[0].name.trim()} ${oosText}`);
      setVariantChanged(true);
      setOptionOutOfStock(chosenVariant.total_on_hand === 0);
      updateWarningMessage('');
    } else {
      // to clear the option if item was added to cart via on page
      setOption(null);
      setVariantChanged(false);
    }
  }, [props.variant]);

  useEffect(() => {
    if (buttonText === displayButtonText) return;

    if (buttonText === 'Out of Stock') {
      setDisplayButtonText('Out of Stock - Notify Me');
      setOutOfStock(true);
    } else {
      // to handle if the user switches from an
      // OOS variant to a in-stock variant
      if (buttonText === 'Add to Bag' && (!outOfStock || !optionOutOfStock)) {
        setOutOfStock(false);
        setOptionOutOfStock(false);
      }

      setDisplayButtonText(buttonText);
    }
  }, [buttonText]);

  useEffect(() => {
    setRequireMonogram(contextRequireMonogram);
  }, [contextRequireMonogram]);

  useEffect(() => {
    // if the product was successfully added to the cart
    // then all context values would be false,
    // so reset addingToCart to allow ATB button to be abled
    if (!onPageAddedToCart && !stickyAddedToCart && addingToCart) setAddingToCart(false);
  }, [onPageAddedToCart, stickyAddedToCart]);

  return (
    <StickyMobileProductAdd
      showWarningMsg={warning.length > 0}
      isVisible={isVisible}
      notAvailable={!isProductAvailable() || isProductDiscontinued()}
    >
      {warning.length ? (
        <WarningWrapper>
          <WarningText element="p" like="label-1">
            {warning}
          </WarningText>
        </WarningWrapper>
      ) : null}
      <CallToActionWrapper>

        { hasVariants() && !outOfStock && !optionOutOfStock ? (
          <SizeSelect
            name="size"
            id="sticky-product-add-to-cart-size-select"
            value={!option ? '0' : option.trim()}
            change={variantChanged}
            onChange={handleOnSelectChange}
            outline
            showWarningMsg={warning.length > 0 && !warning.includes('monogram')}
          >
            <option value="0" disabled hidden>{`${optionType}`}</option>
            {renderSizeOptions()}
          </SizeSelect>
        ) : null}
        <ButtonWrapper
          hasVariants={hasVariants() && !outOfStock && !optionOutOfStock}
        >
          <SubmitButton
            type="submit"
            data-test-id="add_to_cart"
            id="sticky-product-add-to-cart-button"
            disabled={buttonDisabled()}
            onClick={handleOnSubmit}
            displayOpacity={!buttonDisabled()}
            green={pdpRevamp}
          >
            <>
              {displayButtonText}
            </>
          </SubmitButton>
        </ButtonWrapper>
      </CallToActionWrapper>
    </StickyMobileProductAdd>
  );
};

StickyProductAdd.defaultProps = {
  cart: {},
  variant: null
};

StickyProductAdd.propTypes = {
  cart: PropTypes.object,
  onVariantChange: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  variant: PropTypes.object
};

const mapStateToProps = (state) => ({
  cart: state.cart
});

export default connect(mapStateToProps, null)(StickyProductAdd);
